import { api } from '../../../services/api/apiClient';
import type { Option } from '../../../types/common';
import type { MasterEntry, MastersByType, MasterType } from '../types/masterTypes';

// Editable dropdown lists (Master data), loaded once and shared by every screen.
let cache: MastersByType | null = null; // { unit: [...], expenseCategory: [...], ... } active entries only
let pending: Promise<MastersByType> | null = null;
const listeners = new Set<(m: MastersByType) => void>();

export function loadMasters(force = false): Promise<MastersByType> {
  if (force) {
    cache = null;
    pending = null;
  }
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = api
      .get<{ data: MastersByType }>('/masters')
      .then((r) => {
        cache = r.data;
        listeners.forEach((fn) => fn(r.data));
        return r.data;
      })
      .catch((err) => {
        pending = null;
        throw err;
      });
  }
  return pending;
}

/** Call after adding or editing master data so every screen sees the change. */
export const refreshMasters = () => loadMasters(true).catch(() => {});

export const mastersLoaded = () => !!cache;

/** Subscribe to master data loads; returns an unsubscribe function. */
export function onMastersChange(fn: (m: MastersByType) => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

const pretty = (v: unknown) => (v == null ? '' : String(v).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()));

/** Label for a stored value, e.g. masterLabel('expenseCategory', 'fuel') -> "Fuel". Falls back to the value itself. */
export function masterLabel(type: string, value: unknown): string {
  const hit = cache?.[type]?.find((m) => m.value === String(value));
  return hit?.label ?? pretty(value);
}

/** [{ value, label, description }] for a Select. `keep` adds a current value that is no longer in the list. */
export function masterOptions(type: string, { keep, numeric }: { keep?: string | number | null; numeric?: boolean } = {}): Option<any>[] {
  const list: Option<any>[] = (cache?.[type] ?? []).map((m) => ({
    value: numeric ? Number(m.value) : m.value,
    label: m.label,
    description: m.type === 'hsnSac' ? `${m.description ?? ''}${m.gstRate != null ? ` · GST ${m.gstRate}%` : ''}` : m.description,
  }));
  if (keep != null && keep !== '' && !list.some((o) => String(o.value) === String(keep))) list.unshift({ value: keep, label: String(keep) });
  return list;
}

export const masterEntry = (type: string, value: unknown): MasterEntry | undefined => cache?.[type]?.find((m) => m.value === String(value));

// ---- CRUD for the Master data screens
export const masterService = {
  /** Active entries grouped by type: { unit: [...], ... } */
  grouped: () => api.get<{ data: MastersByType }>('/masters').then((r) => r.data),
  /** Every entry of one type, including hidden ones. */
  listType: (type: string) => api.get<{ data: MasterEntry[] }>('/masters', { type, includeInactive: 'true' }).then((r) => r.data),
  get: (id: string) => api.get<{ data: MasterEntry }>(`/masters/${id}`).then((r) => r.data),
  create: (body: Partial<MasterEntry> & { type: MasterType; label: string }) => api.post<{ data: MasterEntry }>('/masters', body).then((r) => r.data),
  update: (id: string, body: Partial<MasterEntry>) => api.patch<{ data: MasterEntry }>(`/masters/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.del(`/masters/${id}`),
};
